import type { AIAnswerContent } from "../../types/page-content";

interface Props {
  content: AIAnswerContent;
  sectionNumber?: string;
}

export default function AIAnswerSection({ content, sectionNumber = "03" }: Props) {
  return (
    <section className="bg-[#161616] py-28 lg:py-40 relative overflow-hidden">
      <div className="absolute right-6 top-10 hidden xl:block">
        <span className="font-heading text-white/[0.03] text-[180px] leading-none select-none">{sectionNumber}</span>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-14">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-20">
          {/* Left col — 3/5 */}
          <div className="lg:col-span-3">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-0.5 bg-safety-500" />
              <span className="font-body text-safety-500 text-xs uppercase tracking-widest2 font-700">{content.eyebrow}</span>
            </div>
            <h2 className="font-heading text-white text-5xl lg:text-7xl tracking-display leading-none mb-8">
              {content.mainQuestion}
            </h2>
            <p className="font-body text-asphalt-200 text-base leading-relaxed mb-10">
              {content.mainAnswer}
            </p>

            <h3 className="font-heading text-white text-3xl tracking-display mb-6">{content.listHeading}</h3>
            <ul className="flex flex-col gap-4">
              {content.listItems.map((item) => (
                <li key={item} className="flex items-start gap-4">
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <div className="w-1.5 h-1.5 bg-safety-500 rounded-full ring-2 ring-safety-500/30" />
                  </div>
                  <span className="font-body text-asphalt-200 text-sm leading-relaxed">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Right col — 2/5 */}
          <div className="lg:col-span-2">
            <div className="border border-asphalt-600 bg-[#111111] p-8 lg:p-10 h-full flex flex-col">
              <div className="w-10 h-0.5 bg-safety-500 mb-6" />
              <h3 className="font-heading text-white text-3xl tracking-display mb-5">{content.sideQuestion}</h3>
              <p className="font-body text-asphalt-300 text-sm leading-relaxed mb-8 flex-1">
                {content.sideAnswer}
              </p>

              <div className="flex flex-col gap-4 mb-8">
                {content.sidePoints.map((pt) => (
                  <div key={pt.label} className="flex items-center gap-4 border-b border-asphalt-600 pb-4 last:border-0 last:pb-0">
                    <div className="w-10 h-10 flex items-center justify-center bg-safety-500/10 border border-safety-500/20 flex-shrink-0">
                      <i className={`${pt.icon} text-safety-500 text-base`} />
                    </div>
                    <span className="font-body text-asphalt-200 text-sm">{pt.label}</span>
                  </div>
                ))}
              </div>

              <a
                href="tel:5558007283"
                className="flex items-center justify-center gap-2 bg-safety-500 text-asphalt-950 font-body font-700 text-xs uppercase tracking-widest py-4 hover:bg-safety-400 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-phone-fill" />
                Call for a Consultation
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
